import React from 'react'
import styled from 'styled-components'
import { Table, useMediaSize, get } from '@ally/metronome-ui'
import { Account } from '@ally/data-types-afg-accounts'
import AccountGroup from '../../Shared/AccountGroup'
import AccountCard from '../../Shared/AccountCard'
import { useCustomizeDashboard } from '../../CustomizeDashboard/CustomizeDashboardContext/CustomizeDashboardContext'
import { useAccountTypes } from '../../AccountTypeContext'
import { getColumns, getFooter } from './TableColumns'
import MobileDepositAccountList from '../../Shared/MobileAccountsCard/MobileDepositAccountList'
import AccountCardMbox from '../../Shared/AccountCardMbox'
import { sortDepositAccountsGroup, hasStaleAccounts } from '../../utils'
import { NonPrintingWrapper } from '../../Shared/NonPrintingWrapper'
import { useHostServices } from '../../HostServices'

const StyledDiv = styled.div`
  border-top: 1px solid ${get.colors('slate-2')};
`

const FooterTableWrapper = styled.div`
  table {
    thead {
      tr {
        th {
          height: 0;
        }
      }
    }
    tbody {
      display: none;
    }
    tfoot {
      tr {
        td:first-child {
          width: 24%;
        }
      }
    }
  }
`

const IRAs: React.FC = () => {
  const [{ dashboardView }] = useCustomizeDashboard()
  const { isBankOutage, activeDepositAccounts } = useAccountTypes()
  const hasStaleData = hasStaleAccounts(activeDepositAccounts)

  const { featureFlags } = useHostServices()
  const isProductNameChangeEnabled = featureFlags.variation(
    'FF_product-name-change',
    false,
  )

  const retirementAccounts = activeDepositAccounts.filter(
    acc => !!acc.indicator?.retirement,
  )

  const sdaIraAccountsGroup = sortDepositAccountsGroup(
    retirementAccounts.filter(acc => acc.type === 'SDA'),
  )
  const cdaIraAccountsGroup = sortDepositAccountsGroup(
    retirementAccounts.filter(acc => acc.type === 'CDA'),
  )
  const sortedIraAccounts: Account[] = [
    ...sdaIraAccountsGroup.activeAccounts,
    ...cdaIraAccountsGroup.activeAccounts,
    ...sdaIraAccountsGroup.closedAccounts,
    ...cdaIraAccountsGroup.closedAccounts,
  ]

  const sepAccounts = sortedIraAccounts.filter(
    account => account.meta?.retirementPlanType === 'SEP',
  )
  const rothAccounts = sortedIraAccounts.filter(
    account => account.meta?.retirementPlanType === 'Roth',
  )
  const traditionalAccounts = sortedIraAccounts.filter(
    account => account.meta?.retirementPlanType === 'Traditional',
  )

  const hasSEPAccounts = sepAccounts.length > 0
  const hasRothAccounts = rothAccounts.length > 0
  const hasTraditionalAccounts = traditionalAccounts.length > 0

  const accountType = sortedIraAccounts.map(account => account.type)
  const allSame = accountType.every((type: string) => type === accountType[0])

  const isMdDown = useMediaSize('MdDown')
  const isLgDown = useMediaSize('LgDown')
  const isPrint = window.matchMedia('print').matches

  return (
    <>
      {sortedIraAccounts.length && !isBankOutage ? (
        <AccountGroup name="IRAs">
          <AccountCard type="IRA">
            {/* SEP Table */}
            {hasSEPAccounts &&
              (isMdDown && !isPrint ? (
                <MobileDepositAccountList
                  accounts={sepAccounts}
                  isIRA
                  hasStaleData={hasStaleData}
                />
              ) : (
                <Table
                  data={sepAccounts}
                  columns={getColumns({
                    planType: 'SEP',
                    view: dashboardView,
                    isLgDown,
                    hasStaleData,
                    isProductNameChangeEnabled,
                  })}
                  captionText="Information about your Ally Bank accounts"
                  fullWidth
                  suppressMdDownLayout
                />
              ))}
            {hasSEPAccounts && (hasRothAccounts || hasTraditionalAccounts) ? (
              <StyledDiv />
            ) : null}

            {/* Roth Table */}
            {hasRothAccounts &&
              (isMdDown && !isPrint ? (
                <MobileDepositAccountList
                  accounts={rothAccounts}
                  isIRA
                  hasStaleData={hasStaleData}
                />
              ) : (
                <Table
                  data={rothAccounts}
                  columns={getColumns({
                    planType: 'Roth',
                    view: dashboardView,
                    isLgDown,
                    hasStaleData,
                    isProductNameChangeEnabled,
                  })}
                  captionText="Information about your Ally Bank accounts"
                  fullWidth
                  suppressMdDownLayout
                />
              ))}
            {hasRothAccounts && hasTraditionalAccounts ? <StyledDiv /> : null}

            {/* Traditional Table */}
            {hasTraditionalAccounts &&
              (isMdDown && !isPrint ? (
                <MobileDepositAccountList
                  accounts={traditionalAccounts}
                  isIRA
                  hasStaleData={hasStaleData}
                />
              ) : (
                <Table
                  data={traditionalAccounts}
                  columns={getColumns({
                    planType: 'Traditional',
                    view: dashboardView,
                    isLgDown,
                    hasStaleData,
                    isProductNameChangeEnabled,
                  })}
                  captionText="Information about your Ally Bank accounts"
                  fullWidth
                  suppressMdDownLayout
                />
              ))}
            {/* Table Footer */}
            {allSame &&
              (isMdDown && !isPrint ? (
                <MobileDepositAccountList
                  accounts={sortedIraAccounts}
                  isIRAFooter
                  hasStaleData={hasStaleData}
                />
              ) : (
                <FooterTableWrapper>
                  <Table
                    data={sortedIraAccounts}
                    columns={getFooter({ view: dashboardView })}
                    captionText="Information about your Ally Bank accounts"
                    fullWidth
                    suppressMdDownLayout
                    withFooter
                  />
                </FooterTableWrapper>
              ))}
            <NonPrintingWrapper>
              <AccountCardMbox name="ally-dashboard-ira-well" />
            </NonPrintingWrapper>
          </AccountCard>
        </AccountGroup>
      ) : null}
    </>
  )
}

export default IRAs
